import React from "react"
import autobind from "class-autobind"
import { Card, CardText, Paper, Tabs, Tab, TextField, CircularProgress, FontIcon, FloatingActionButton, Drawer, FlatButton } from "material-ui"
import ClearIcon from "material-ui/svg-icons/content/clear"
import ContentAdd from "material-ui/svg-icons/content/add"
import strings from "../../localization/strings"
import { IApprovalValidationType, IApprovalValidation, IDate, INoteDocument, INoteObjectData, INoteSearch, INoteSearchParam } from "../../models"
import * as api from "../../api"
import DateRangeSelect from "../../components/common/DateRangeSelect"
import AccountStore from "../../stores/AccountStore"
import NotesList from "./NotesList"
import AddNewNote from "./AddNewNote"
import AppStateStore from "../../stores/AppStateStore"
import moment from "moment"

interface IProps {
    objectData: INoteObjectData
    objectName: string
    dataType: string
    validationMessages?: IApprovalValidation[]
    readValidationMessage: (msgeId: string) => Promise<IApprovalValidation>
}
interface IState {
    audits: INoteDocument[]
    notes: INoteDocument[]
    searchText: string
    startDate: IDate
    endDate: IDate
    loadingNotes: boolean
    loadingAudits: boolean
    showDateFilter: boolean
    total: number
    offset: number
    limit: number
    limitOptions: number[]
    openAddNoteDrawer: boolean
    readingMessage: string
    validationMessages: IApprovalValidation[]
}


export default class Notes extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)
        this.state = {
            notes: [],
            audits: [],
            searchText: "",
            startDate: null,
            endDate: null,
            loadingNotes: false,
            loadingAudits: false,
            showDateFilter: true,
            total: 0,
            offset: 0,
            limit: 5,
            limitOptions: [5, 10],
            openAddNoteDrawer: false,
            readingMessage: "",
            validationMessages: props.validationMessages
        }
        autobind(this)
    }

    componentDidMount() {
        this.getNotes()
        this.getAudits()
        this.setState({ validationMessages: this.props.validationMessages })
    }

    getAudits() {
        this.setState({ loadingAudits: true }, () => {
            const notesPayload = {
                AccountId: AccountStore.getCurrentAccount().Id,
                ObjectId: this.props.objectData.ObjectId,
                SearchText: this.state.searchText,
                ModifiedStartDate: this.state.startDate,
                ModifiedEndDate: this.state.endDate,
                Page: this.state.offset / this.state.limit,
                Limit: this.state.limit,
                Audits: true,
                NoteTimecardRef: this.props.objectData.TimecardRef
            } as INoteSearchParam
            api.getNotes(notesPayload).then(res => {
                const data = res.data as INoteSearch
                this.setState({
                    loadingAudits: false,
                    total: data.TotalDocumentCount,
                    audits: data.Documents
                })
            })
        })
    }

    getNotes() {
        this.setState({ loadingNotes: true }, () => {
            const notesPayload = {
                AccountId: AccountStore.getCurrentAccount().Id,
                ObjectId: this.props.objectData.ObjectId ? this.props.objectData.ObjectId : "",
                SearchText: this.state.searchText,
                ModifiedStartDate: this.state.startDate,
                ModifiedEndDate: this.state.endDate,
                Page: this.state.offset / this.state.limit,
                Limit: this.state.limit,
                Audits: false,
                NoteTimecardRef: this.props.objectData.TimecardRef
            } as INoteSearchParam
            api.getNotes(notesPayload).then(res => {
                const data = res.data as INoteSearch
                this.setState({
                    loadingNotes: false,
                    total: data.TotalDocumentCount,
                    notes: data.Documents
                })
            })
        })
    }

    onMarkAsRead(msg: IApprovalValidation) {
        this.setState({ readingMessage: msg.Id })
        this.props.readValidationMessage(msg.Id).then((updatedMsg: IApprovalValidation) => {
            this.setState((prevState) => {
                const updatedMessages = prevState.validationMessages.map((item) => {
                    if (item.Id === updatedMsg.Id) {
                        return { ...item, ...updatedMsg }
                    }
                    return item
                })
                return { ...prevState, validationMessages: updatedMessages, readingMessage: "" }
            })
        })
    }

    render() {
        return (
            <Paper data-intercom-target={"Notes-Drawer"} zDepth={1} style={{ textAlign: "left" }}>
                <Tabs>
                    {this.state.validationMessages && this.state.validationMessages.length > 0 &&
                        <Tab data-intercom-target={"validation-Tab"} label={"Validation Messages"} value="validation" >
                            <div style={{ overflow: "auto", height: "calc(100vh - 138px)" }}>
                                {this.state.validationMessages.map((msg, index) => {
                                    let icon = (<i className="fa-solid fa-circle-exclamation" style={{ marginRight: 5, color: "#f44336" }} />)
                                    if (msg.ApprovalValidationType === 1) {
                                        icon = <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 5, color: "#ffc400" }} />
                                    } else if (msg.ApprovalValidationType === 2) {
                                        icon = <i className="fa-solid fa-circle-info" style={{ marginRight: 5, color: "#007afe" }} />
                                    }

                                    return (
                                        <Card key={index} style={{ margin: 20, padding: 20 }}>
                                            <div style={{ display: "grid", alignItems: "center", gridTemplateColumns: "min-content auto max-content" }}>
                                                <div style={{ gridColumn: 1, gridRow: 1, fontSize: 35, paddingRight: 20 }}>
                                                    {icon}
                                                </div>
                                                <div style={{ gridColumn: 2, gridRow: 1 }}>
                                                    <div style={{ gridColumn: 2, gridRow: 1 }}>
                                                        <h3>{msg.Name}</h3>
                                                        <p>{msg.Details}</p>
                                                    </div>
                                                </div>
                                                {[IApprovalValidationType.Info, IApprovalValidationType.Warning].includes(msg.ApprovalValidationType) && <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: "217px" }}>
                                                    {this.state.readingMessage === msg.Id ? <div style={{ height: "36px" }}>
                                                        <CircularProgress />
                                                    </div> : <FlatButton
                                                        data-intercom-target={"menu-Employees-Timecards-Btn-AddOrEditPunches"}
                                                        label={strings.Mark_As_Read}
                                                        backgroundColor={msg.Read ? "#bdbdbd" : AppStateStore.getFrontEndSettings().UISettings.AccentColor}
                                                        hoverColor={AppStateStore.getFrontEndSettings().UISettings.AccentColor}
                                                        style={{ width: "170px" }}
                                                        labelStyle={{ color: "white" }}
                                                        disabled={!!msg.Read}
                                                        onClick={() => this.onMarkAsRead(msg)}
                                                    />}
                                                    {msg.Read && <p>{strings.Reviewed_by}: {`${msg.ReadBy} ${moment(msg.Readon).utc().format("MM/DD/YY")}`} </p>}
                                                </div>}
                                            </div>
                                        </Card>
                                    )
                                })}
                            </div>
                        </Tab>
                    }
                    <Tab data-intercom-target={"Notes-Tab"} label={strings.Notes} value="notes">
                        {!this.state.openAddNoteDrawer &&
                            <Card style={{ boxShadow: "none", overflow: "auto", height: "calc(100vh - 138px)" }}>
                                <CardText>
                                    <FloatingActionButton data-intercom-target={"Btn-Add-Note"} className="fixed-add" style={{ top: -30, right: 40, zIndex: 998 }} secondary={true} onTouchTap={() => this.setState({ openAddNoteDrawer: true })}>
                                        <ContentAdd />
                                    </FloatingActionButton>
                                    <div style={{ margin: 5, minHeight: "90vh" }}>
                                        {
                                            !this.state.notes.length && this.state.startDate === null && this.state.endDate === null && this.state.searchText === "" ?
                                                <div></div> :
                                                <div>
                                                    {/* Date Filter */}
                                                    <div style={{ display: "flex" }}>
                                                        {this.state.showDateFilter &&
                                                            <DateRangeSelect
                                                                startDate={this.state.startDate}
                                                                endDate={this.state.endDate}
                                                                sameDatesAllowed
                                                                handleChange={(startDate, endDate, isValid) => isValid && this.setState({ startDate, endDate }, () =>
                                                                    this.getNotes()
                                                                )}
                                                            />
                                                        }
                                                        {(this.state.startDate !== null || this.state.endDate !== null) && (
                                                            <ClearIcon
                                                                data-intercom-target={"Notes-Btn-Clear-Date-Filter"}
                                                                style={{ marginLeft: "1%", marginTop: "4.5%", cursor: "pointer" }}
                                                                onClick={() => this.setState({ startDate: null, endDate: null }, () => {
                                                                    this.getNotes()
                                                                    this.setState({ showDateFilter: false }, () => this.setState({ showDateFilter: true }))
                                                                })}
                                                            />
                                                        )}
                                                    </div>

                                                    {/* Note Search */}
                                                    <div style={{ marginTop: "8%" }}>
                                                        <i style={{ fontSize: 25 }} className="fa fa-search" />
                                                        <TextField
                                                            data-intercom-target={"Notes-Entry-Search"}
                                                            hintText={strings.Search}
                                                            style={{ marginRight: 20, width: "90%", marginLeft: 15 }}
                                                            value={this.state.searchText}
                                                            onChange={e => this.setState({ searchText: e.target.value }, () => this.getNotes())}
                                                        />
                                                        {(this.state.searchText !== "") && (
                                                            <ClearIcon
                                                                data-intercom-target={"Notes-Btn-Clear-SearchText"}
                                                                style={{ marginBottom: -15, cursor: "pointer" }}
                                                                onClick={() => this.setState({ searchText: "" }, () => this.getNotes())}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                        }
                                        {/* Notes List */}
                                        {this.state.loadingNotes ?
                                            <div className="spinner"><CircularProgress /></div> :
                                            (!this.state.notes.length && this.state.startDate === null && this.state.endDate === null && this.state.searchText === "" ?
                                                <div style={{ textAlign: "center", marginTop: "20%" }}>
                                                    <h2>{strings.Enter_First_Note}</h2>
                                                    <FontIcon className="fa fa-comments" style={{ fontSize: 150, color: "#95989a", }} />
                                                </div>
                                                :
                                                (!this.state.notes.length && ((this.state.startDate !== null || this.state.endDate !== null) && this.state.searchText === "")) ?
                                                    <div style={{ textAlign: "center", marginTop: "20%" }}>
                                                        <h2>{strings.No_Match_Filter}</h2>
                                                        <FontIcon className="fa fa-comments" style={{ fontSize: 150, color: "#95989a", }} />
                                                    </div>
                                                    :
                                                    (!this.state.notes.length && ((this.state.startDate !== null || this.state.endDate !== null) && this.state.searchText !== "")) ?
                                                        <div style={{ textAlign: "center", marginTop: "20%" }}>
                                                            <h2>{strings.No_Search_Result}</h2>
                                                            <FontIcon className="fa fa-comments" style={{ fontSize: 150, color: "#95989a", }} />
                                                        </div>
                                                        :
                                                        (!this.state.notes.length && ((this.state.startDate === null || this.state.endDate === null) && this.state.searchText !== "")) ?
                                                            <div style={{ textAlign: "center", marginTop: "20%" }}>
                                                                <h2>{strings.No_Search_Result}</h2>
                                                                <FontIcon className="fa fa-comments" style={{ fontSize: 150, color: "#95989a", }} />
                                                            </div>
                                                            :
                                                            <NotesList
                                                                simpleList={false}
                                                                notes={this.state.notes}
                                                                total={this.state.total}
                                                                offset={this.state.offset}
                                                                limit={this.state.limit}
                                                                limitOptions={this.state.limitOptions}
                                                                onPageClick={offset => this.setState({ offset }, () => this.getNotes())}
                                                                onLimitChange={limit => this.setState({ offset: 0, limit }, () => this.getNotes())}
                                                            />
                                            )
                                        }
                                    </div>
                                </CardText>
                            </Card>
                        }
                        {this.state.openAddNoteDrawer &&
                            <Drawer
                                open={this.state.openAddNoteDrawer}
                                width={1000}
                                style={{ maxWidth: 1000 }}
                                openSecondary
                                docked={false}
                                containerStyle={{
                                    opacity: open ? 1 : 0,
                                    transition: "opacity 200ms, transform 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms"
                                }}
                            >
                                <AddNewNote
                                    objectData={this.props.objectData}
                                    objectName={this.props.objectName}
                                    getNotes={this.getNotes}
                                    closeDrawer={() => this.setState({ openAddNoteDrawer: false })}
                                    dataType={this.props.dataType}
                                />
                            </Drawer>
                        }
                    </Tab>
                    <Tab data-intercom-target={"Audits-Tab"} label={strings.Audits} value="audits">
                        {!this.state.openAddNoteDrawer &&
                            <Card style={{ boxShadow: "none", overflow: "auto", height: "calc(100vh - 138px)" }}>
                                <CardText>
                                    <div style={{ margin: 5, minHeight: "90vh" }}>
                                        {
                                            !this.state.audits.length && this.state.startDate === null && this.state.endDate === null && this.state.searchText === "" ?
                                                <div></div> :
                                                <div>
                                                    {/* Date Filter */}
                                                    <div style={{ display: "flex" }}>
                                                        {this.state.showDateFilter &&
                                                            <DateRangeSelect
                                                                startDate={this.state.startDate}
                                                                endDate={this.state.endDate}
                                                                sameDatesAllowed
                                                                handleChange={(startDate, endDate, isValid) => isValid && this.setState({ startDate, endDate }, () =>
                                                                    this.getAudits()
                                                                )}
                                                            />
                                                        }
                                                        {(this.state.startDate !== null || this.state.endDate !== null) && (
                                                            <ClearIcon
                                                                data-intercom-target={"Audits-Btn-Clear-Date-Filter"}
                                                                style={{ marginLeft: "1%", marginTop: "4.5%", cursor: "pointer" }}
                                                                onClick={() => this.setState({ startDate: null, endDate: null }, () => {
                                                                    this.getAudits()
                                                                    this.setState({ showDateFilter: false }, () => this.setState({ showDateFilter: true }))
                                                                })}
                                                            />
                                                        )}
                                                    </div>

                                                    {/* Note Search */}
                                                    <div style={{ marginTop: "8%" }}>
                                                        <i style={{ fontSize: 25 }} className="fa fa-search" />
                                                        <TextField
                                                            data-intercom-target={"Audits-Entry-Search"}
                                                            hintText={strings.Search}
                                                            style={{ marginRight: 20, width: "90%", marginLeft: 15 }}
                                                            value={this.state.searchText}
                                                            onChange={e => this.setState({ searchText: e.target.value }, () => this.getAudits())}
                                                        />
                                                        {(this.state.searchText !== "") && (
                                                            <ClearIcon
                                                                data-intercom-target={"Audits-Btn-Clear-SearchText"}
                                                                style={{ marginBottom: -15, cursor: "pointer" }}
                                                                onClick={() => this.setState({ searchText: "" }, () => this.getAudits())}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                        }
                                        {/* Audits List */}
                                        {this.state.loadingAudits ?
                                            <div className="spinner"><CircularProgress /></div> :
                                            (!this.state.audits.length && this.state.startDate === null && this.state.endDate === null && this.state.searchText === "" ?
                                                <div style={{ textAlign: "center", marginTop: "20%" }}>
                                                    <h2>{strings.No_Audits}</h2>
                                                    <FontIcon className="fa fa-comments" style={{ fontSize: 150, color: "#95989a", }} />
                                                </div>
                                                :
                                                (!this.state.audits.length && ((this.state.startDate !== null || this.state.endDate !== null) && this.state.searchText === "")) ?
                                                    <div style={{ textAlign: "center", marginTop: "20%" }}>
                                                        <h2>{strings.No_Match_Filter}</h2>
                                                        <FontIcon className="fa fa-comments" style={{ fontSize: 150, color: "#95989a", }} />
                                                    </div>
                                                    :
                                                    (!this.state.audits.length && ((this.state.startDate !== null || this.state.endDate !== null) && this.state.searchText !== "")) ?
                                                        <div style={{ textAlign: "center", marginTop: "20%" }}>
                                                            <h2>{strings.No_Search_Result}</h2>
                                                            <FontIcon className="fa fa-comments" style={{ fontSize: 150, color: "#95989a", }} />
                                                        </div>
                                                        :
                                                        (!this.state.audits.length && ((this.state.startDate === null || this.state.endDate === null) && this.state.searchText !== "")) ?
                                                            <div style={{ textAlign: "center", marginTop: "20%" }}>
                                                                <h2>{strings.No_Search_Result}</h2>
                                                                <FontIcon className="fa fa-comments" style={{ fontSize: 150, color: "#95989a", }} />
                                                            </div>
                                                            :
                                                            <NotesList
                                                                simpleList={true}
                                                                notes={this.state.audits}
                                                                total={this.state.total}
                                                                offset={this.state.offset}
                                                                limit={this.state.limit}
                                                                limitOptions={this.state.limitOptions}
                                                                onPageClick={offset => this.setState({ offset }, () => this.getAudits())}
                                                                onLimitChange={limit => this.setState({ offset: 0, limit }, () => this.getAudits())}
                                                            />
                                            )
                                        }
                                    </div>
                                </CardText>
                            </Card>
                        }
                    </Tab>
                </Tabs>
            </Paper>
        )
    }
}